import { useBoolean } from "@/hooks/use-boolean";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import FormSaveButton from "@babackapp/react-hook-forms-components/form-save-button";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import { Form } from "@babackapp/ui/components/form";
import { Tabs, TabsContent } from "@babackapp/ui/components/tabs";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import ImportFormTabs from "./import-form-tabs";
import ImportMappingsForm from "./import-mappings-form";
import ImportOptionsForm from "./import-options-form";
import ImportServerForm from "./import-server-form";
import {
  ShopImportFormValues,
  ShopImportFormValuesSchema,
} from "./import-settings-form-types";

type ImportSettingsFormProps = {
  onValidate?: (values: ShopImportFormValues) => Promise<void> | void;
  onSubmit: (values: ShopImportFormValues) => void;
  onRemove?: () => void;
  defaultValues?: ShopImportFormValues;
};

export default function ImportSettingsForm({
  onSubmit,
  onRemove,
  defaultValues,
}: ImportSettingsFormProps) {
  const isLoading = useBoolean(false);
  const [error, setError] = useState<string | null>(null);
  // const { data: shop } = useCurrentShopSuspense();
  // const models = shop.imports;
  const t = useTranslation();

  const form = useForm<ShopImportFormValues>({
    resolver: zodResolver(ShopImportFormValuesSchema),
    defaultValues: {
      server: {
        type: "ftp",
        login: {
          user: "",
          password: "",
          host: "",
          path: "",
        },
      },
      options: {
        exampleFile: null,
        settings: {
          delimiter: ",",
          withHeader: true,
          fileToImportFilterRegex: "",
          canImportOrderTwice: false,
          rejectRemainingLineItems: false,
          canCreateReturnRequest: false,
          afterImport: "delete",
        },
      },
      mapping: [],
      ...defaultValues,
    },
  });

  const _onSubmit = async (data: ShopImportFormValues) => {
    try {
      // await onValidate?.(data.server);
      return onSubmit(data);
    } catch (error: unknown) {
      setError((error as Error)?.message || "Unknown error");
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(_onSubmit)}
        className="flex gap-8 flex-col"
      >
        {error && <FormGlobalError error={error} />}
        <Tabs defaultValue="server">
          <ImportFormTabs />
          <TabsContent value="server">
            <ImportServerForm />
          </TabsContent>
          <TabsContent value="options">
            <ImportOptionsForm />
          </TabsContent>
          <TabsContent value="mapping">
            <ImportMappingsForm
              isLoading={isLoading.value}
              setIsLoading={isLoading.setValue}
            />
          </TabsContent>
        </Tabs>
        <div className="flex gap-2 justify-center items-center">
          {onRemove && (
            <Button variant="destructive" onClick={onRemove}>
              {t("misc.delete")}
            </Button>
          )}
          <FormSaveButton />
        </div>
      </form>
    </Form>
  );
}
